import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Monitor,
  Plus,
  Search,
  MapPin,
  MoreVertical,
  Pencil,
  Trash2,
  Activity,
  Wifi,
  WifiOff,
  Wrench,
  Grid3X3,
  List,
  Map,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons by status - using colored icons from CDN
const createMarkerIcon = (status) => {
  const iconColors = {
    online: 'green',
    offline: 'red',
    maintenance: 'orange',
  };
  const colorName = iconColors[status] || 'blue';
  
  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${colorName}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Map bounds fitter component
function FitBounds({ screens }) {
  const map = useMap();
  
  useEffect(() => {
    if (screens.length > 0) {
      console.log('FitBounds screens:', screens.map(s => ({ name: s.name, lat: s.latitude, lng: s.longitude })));
      const validScreens = screens.filter(s => 
        s.latitude && s.longitude && 
        !isNaN(s.latitude) && !isNaN(s.longitude) &&
        Math.abs(s.latitude) <= 90 && Math.abs(s.longitude) <= 180
      );
      if (validScreens.length > 0) {
        const bounds = L.latLngBounds(validScreens.map(s => [s.latitude, s.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [screens, map]);
  
  return null;
}

export default function ScreensPage() {
  const { user, isAdmin, isOperator } = useAuth();
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    latitude: 0,
    longitude: 0,
    width: 1920,
    height: 1080,
    resolution: '1920x1080',
    type: 'outdoor',
    status: 'offline',
    daily_rate: 0,
    tags: [],
  });

  // WebSocket for real-time updates
  const wsRef = useRef(null);

  useEffect(() => {
    loadScreens();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = () => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/ws/screens`);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'screen_status') {
        setScreens((prev) => {
          return prev.map((screen) => {
            const update = data.screens.find((s) => s.id === screen.id);
            if (update) {
              return { ...screen, ...update };
            }
            return screen;
          });
        });
      }
    };
    
    wsRef.current.onerror = () => {
      // Silent fail - WebSocket is optional
    };
    
    wsRef.current.onclose = () => {
      // Reconnect after 5 seconds
      setTimeout(connectWebSocket, 5000);
    };
  };

  const loadScreens = async () => {
    try {
      const response = await api.get('/screens');
      setScreens(response.data);
    } catch (error) {
      toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingScreen) {
        await api.put(`/screens/${editingScreen.id}`, formData);
        toast.success('Screen updated successfully');
      } else {
        await api.post('/screens', formData);
        toast.success('Screen created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadScreens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (screenId) => {
    if (!window.confirm('Are you sure you want to delete this screen?')) return;
    try {
      await api.delete(`/screens/${screenId}`);
      toast.success('Screen deleted successfully');
      loadScreens();
    } catch (error) {
      toast.error('Failed to delete screen');
    }
  };

  // Bulk operations
  const toggleScreenSelection = (screenId) => {
    setSelectedScreens((prev) =>
      prev.includes(screenId)
        ? prev.filter((id) => id !== screenId)
        : [...prev, screenId]
    );
  };

  const selectAllScreens = () => {
    if (selectedScreens.length === filteredScreens.length) {
      setSelectedScreens([]);
    } else {
      setSelectedScreens(filteredScreens.map((s) => s.id));
    }
  };

  const handleBulkAction = async (action, status = null) => {
    if (selectedScreens.length === 0) {
      toast.error('No screens selected');
      return;
    }
    
    if (action === 'delete' && !window.confirm(`Are you sure you want to delete ${selectedScreens.length} screens?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const payload = {
        screen_ids: selectedScreens,
        action: action,
        status: status,
      };
      const response = await api.post('/screens/bulk', payload);
      toast.success(response.data.message);
      setSelectedScreens([]);
      loadScreens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleEdit = (screen) => {
    setEditingScreen(screen);
    setFormData({
      name: screen.name,
      location: screen.location,
      latitude: screen.latitude,
      longitude: screen.longitude,
      width: screen.width,
      height: screen.height,
      resolution: screen.resolution,
      type: screen.type,
      status: screen.status,
      daily_rate: screen.daily_rate,
      tags: screen.tags,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingScreen(null);
    setFormData({
      name: '',
      location: '',
      latitude: 0,
      longitude: 0,
      width: 1920,
      height: 1080,
      resolution: '1920x1080',
      type: 'outdoor',
      status: 'offline',
      daily_rate: 0,
      tags: [],
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online':
        return (
          <Badge variant="outline" className="status-online">
            <Wifi className="w-3 h-3 mr-1" />
            Online
          </Badge>
        );
      case 'offline':
        return (
          <Badge variant="outline" className="status-offline">
            <WifiOff className="w-3 h-3 mr-1" />
            Offline
          </Badge>
        );
      case 'maintenance':
        return (
          <Badge variant="outline" className="status-maintenance">
            <Wrench className="w-3 h-3 mr-1" />
            Maintenance
          </Badge>
        );
      default:
        return null;
    }
  };

  const filteredScreens = screens.filter((screen) => {
    const matchesSearch = screen.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      screen.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || screen.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screens-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Screens
          </h1>
          <p className="text-zinc-400 mt-1">Manage your LED billboard network</p>
        </div>
        {(isAdmin || isOperator) && (
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500 btn-glow"
            data-testid="add-screen-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Screen
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            placeholder="Search screens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            data-testid="search-screens"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-white" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {(isAdmin || isOperator) && selectedScreens.length > 0 && (
        <div className="glass-card border-zinc-800 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
              {selectedScreens.length} selected
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedScreens([])}
              className="border-zinc-700 text-zinc-400"
            >
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-700"
                  disabled={bulkActionLoading}
                >
                  Set Status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                <DropdownMenuItem onClick={() => handleBulkAction('update_status', 'online')} className="cursor-pointer">
                  <Wifi className="w-4 h-4 mr-2 text-emerald-400" />
                  Set Online
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction('update_status', 'offline')} className="cursor-pointer">
                  <WifiOff className="w-4 h-4 mr-2 text-red-400" />
                  Set Offline
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction('update_status', 'maintenance')} className="cursor-pointer">
                  <Wrench className="w-4 h-4 mr-2 text-amber-400" />
                  Set Maintenance
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('delete')}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              disabled={bulkActionLoading}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="grid" className="data-[state=active]:bg-zinc-800" data-testid="grid-view-tab">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Grid
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-zinc-800" data-testid="list-view-tab">
            <List className="w-4 h-4 mr-2" />
            List
          </TabsTrigger>
          <TabsTrigger value="map" className="data-[state=active]:bg-zinc-800" data-testid="map-view-tab">
            <Map className="w-4 h-4 mr-2" />
            Map
          </TabsTrigger>
        </TabsList>

        {/* Grid View */}
        <TabsContent value="grid" className="mt-4">
          {filteredScreens.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Monitor className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No screens found</h3>
              <p className="text-zinc-400 mb-6">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first screen'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredScreens.map((screen) => (
                <Card key={screen.id} className={`glass-card border-zinc-800 card-hover relative ${selectedScreens.includes(screen.id) ? 'ring-2 ring-blue-500' : ''}`} data-testid={`screen-card-${screen.id}`}>
                  {/* Selection checkbox */}
                  {(isAdmin || isOperator) && (
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedScreens.includes(screen.id)}
                        onChange={() => toggleScreenSelection(screen.id)}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  <div className="aspect-video bg-zinc-800 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                    <Monitor className="w-16 h-16 text-zinc-700" />
                    <div className="absolute top-3 right-3">{getStatusBadge(screen.status)}</div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <Activity className="w-3 h-3 text-emerald-500" />
                        <span className="font-mono">{screen.uptime_percentage?.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{screen.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-zinc-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{screen.location}</span>
                        </div>
                      </div>
                      {(isAdmin || isOperator) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                            <DropdownMenuItem onClick={() => handleEdit(screen)} className="cursor-pointer">
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(screen.id)}
                                className="text-rose-400 focus:text-rose-400 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-zinc-400">{screen.resolution}</span>
                      <Badge variant="outline" className="text-xs bg-zinc-800 border-zinc-700">
                        {screen.type}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
                      <span className="text-sm text-zinc-500">${screen.daily_rate}/day</span>
                      <span className="text-sm text-emerald-500">{screen.uptime_percentage?.toFixed(1)}% uptime</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Screen ID</TableHead>
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500">Location</TableHead>
                  <TableHead className="text-zinc-500">Status</TableHead>
                  <TableHead className="text-zinc-500">Resolution</TableHead>
                  <TableHead className="text-zinc-500">Type</TableHead>
                  <TableHead className="text-zinc-500">Daily Rate</TableHead>
                  <TableHead className="text-zinc-500">Uptime</TableHead>
                  {(isAdmin || isOperator) && <TableHead className="text-zinc-500 text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredScreens.map((screen) => (
                  <TableRow key={screen.id} className="border-zinc-800" data-testid={`screen-row-${screen.id}`}>
                    <TableCell className="font-mono text-xs text-cyan-400" data-testid={`screen-id-${screen.id}`}>{screen.id}</TableCell>
                    <TableCell className="font-medium text-white">{screen.name}</TableCell>
                    <TableCell className="text-zinc-400">{screen.location}</TableCell>
                    <TableCell>{getStatusBadge(screen.status)}</TableCell>
                    <TableCell className="text-zinc-400">{screen.resolution}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-zinc-800 border-zinc-700">
                        {screen.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400">${screen.daily_rate}</TableCell>
                    <TableCell className="text-emerald-500">{screen.uptime_percentage?.toFixed(1)}%</TableCell>
                    {(isAdmin || isOperator) && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                            <DropdownMenuItem onClick={() => handleEdit(screen)} className="cursor-pointer">
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(screen.id)}
                                className="text-rose-400 focus:text-rose-400 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Map View */}
        <TabsContent value="map" className="mt-4">
          <Card className="glass-card border-zinc-800 overflow-hidden">
            <div className="h-[500px] relative">
              {filteredScreens.filter(s => s.latitude && s.longitude).length > 0 ? (
                <MapContainer
                  center={[39.8283, -98.5795]}
                  zoom={4}
                  style={{ height: '100%', width: '100%' }}
                  className="z-0"
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  />
                  <FitBounds screens={filteredScreens.filter(s => s.latitude && s.longitude)} />
                  {filteredScreens.filter(s => s.latitude && s.longitude).map((screen) => (
                    <Marker
                      key={screen.id}
                      position={[screen.latitude, screen.longitude]}
                      icon={createMarkerIcon(screen.status)}
                      eventHandlers={{
                        click: () => setSelectedScreen(screen),
                      }}
                    >
                      <Popup className="dark-popup">
                        <div className="bg-zinc-900 text-white p-3 rounded-lg min-w-[200px]">
                          <h4 className="font-semibold text-base">{screen.name}</h4>
                          <p className="text-zinc-400 text-sm mt-1">{screen.location}</p>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-700">
                            <span className="text-sm">{screen.resolution}</span>
                            <span className={`text-sm ${
                              screen.status === 'online' ? 'text-emerald-500' :
                              screen.status === 'offline' ? 'text-rose-500' : 'text-amber-500'
                            }`}>
                              {screen.status}
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-emerald-500 text-sm">{screen.uptime_percentage?.toFixed(1)}% uptime</span>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center bg-zinc-900">
                  <div className="text-center">
                    <Map className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                    <p className="text-zinc-400">No screens to display on map</p>
                  </div>
                </div>
              )}
            </div>
            {/* Map Legend */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-emerald-500" />
                  <span className="text-sm text-zinc-400">Online</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-rose-500" />
                  <span className="text-sm text-zinc-400">Offline</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500" />
                  <span className="text-sm text-zinc-400">Maintenance</span>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {editingScreen ? 'Edit Screen' : 'Add New Screen'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-300">Screen Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="Times Square North"
                  required
                  data-testid="screen-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-300">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="New York, NY"
                  required
                  data-testid="screen-location-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">Resolution</Label>
                <Select
                  value={formData.resolution}
                  onValueChange={(value) => setFormData({ ...formData, resolution: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="1920x1080">1920x1080 (FHD)</SelectItem>
                    <SelectItem value="2560x1440">2560x1440 (2K)</SelectItem>
                    <SelectItem value="3840x2160">3840x2160 (4K)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Daily Rate ($)</Label>
                <Input
                  type="number"
                  value={formData.daily_rate}
                  onChange={(e) => setFormData({ ...formData, daily_rate: parseFloat(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="save-screen-btn">
                {editingScreen ? 'Update' : 'Create'} Screen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
